import type { DrawingLine } from '../model';

export interface WebGlLineRenderer {
  readonly available: boolean;
  resize(width: number, height: number): void;
  render(lines: readonly DrawingLine[]): void;
  dispose(): void;
}

function createShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function parseColor(color: string): readonly [number, number, number, number] {
  const match = color.match(/rgba?\(([^)]+)\)/i);
  if (match?.[1]) {
    const values = match[1].split(',').map((value) => Number.parseFloat(value.trim()));
    return [(values[0] ?? 20) / 255, (values[1] ?? 60) / 255, (values[2] ?? 105) / 255, values[3] ?? 0.18];
  }
  return [0.08, 0.24, 0.41, 0.18];
}

export function createWebGlLineRenderer(canvas: HTMLCanvasElement): WebGlLineRenderer {
  const gl = canvas.getContext('webgl', {
    alpha: true,
    antialias: true,
    premultipliedAlpha: true
  });

  if (!gl) {
    return {
      available: false,
      resize: () => undefined,
      render: () => undefined,
      dispose: () => undefined
    };
  }

  const vertexShader = createShader(
    gl,
    gl.VERTEX_SHADER,
    `attribute vec2 a_position;
     void main() {
       gl_Position = vec4(a_position, 0.0, 1.0);
     }`
  );
  const fragmentShader = createShader(
    gl,
    gl.FRAGMENT_SHADER,
    `precision mediump float;
     uniform vec4 u_color;
     void main() {
       gl_FragColor = u_color;
     }`
  );

  const program = gl.createProgram();
  const buffer = gl.createBuffer();

  if (!vertexShader || !fragmentShader || !program || !buffer) {
    return {
      available: false,
      resize: () => undefined,
      render: () => undefined,
      dispose: () => undefined
    };
  }

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    return {
      available: false,
      resize: () => undefined,
      render: () => undefined,
      dispose: () => undefined
    };
  }

  const positionLocation = gl.getAttribLocation(program, 'a_position');
  const colorLocation = gl.getUniformLocation(program, 'u_color');

  const render = (lines: readonly DrawingLine[]): void => {
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (lines.length === 0) return;

    const vertices = new Float32Array(lines.length * 4);
    lines.forEach((line, index) => {
      const offset = index * 4;
      vertices[offset] = line.start.x * 2 - 1;
      vertices[offset + 1] = 1 - line.start.y * 2;
      vertices[offset + 2] = line.end.x * 2 - 1;
      vertices[offset + 3] = 1 - line.end.y * 2;
    });

    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    const owner = canvas.parentElement ?? canvas;
    const color = getComputedStyle(owner).getPropertyValue('--webgl-line').trim();
    const [r, g, b, a] = parseColor(color);
    gl.uniform4f(colorLocation, r, g, b, a);
    gl.lineWidth(1);
    gl.drawArrays(gl.LINES, 0, lines.length * 2);
  };

  return {
    available: true,
    resize(width: number, height: number): void {
      const dpr = Math.max(1, globalThis.devicePixelRatio || 1);
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      gl.viewport(0, 0, canvas.width, canvas.height);
    },
    render,
    dispose(): void {
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
    }
  };
}
